import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Moment } from "moment";
import { Subscription } from "rxjs";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { ContractDetail, ExpiringContractsDirectDetailServiceData, ForecastedExpiringContractsDirectDetailService } from "../../services/contractsKpi/expiringContractsDirectDetail.service";
import { BarChartData, BarChartDataset } from "../../../../dashboardCommon/components/cardContents/barChart.component";
import { ChartDropdownOption } from "../../../../dashboardCommon/components/cardContents/chartDropdown.component";
import { DateColumnDefinition, EmailColumnDefinition, NumberColumnDefinition, PhoneNumberColumnDefinition, TextColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";
import { ContractsDirectDetailData } from "./contractsDirectDetailData";
import { ImpersonationService } from "src/app/auth/apigee/impersonation.service";

export class ExpiringContractsDirectEvolutionData {
    public barChartData: BarChartData;
    public dates: Moment[];

    constructor(barChartData: BarChartData, dates: Moment[]) {
        this.barChartData = barChartData;
        this.dates = dates;
    }
}

@Component({
    selector: 'expiring-contracts-direct-evolution-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class ExpiringContractsDirectEvolutionKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<ForecastedExpiringContractsDirectDetailService, ExpiringContractsDirectDetailServiceData, ChartDropdownOption[], ContractsDirectDetailData>
    implements OnDestroy {
    public getClassName() { return "ExpiringContractsDirectEvolutionKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryDetailService: ForecastedExpiringContractsDirectDetailService) {
        super(zone, logger, summaryDetailService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: ExpiringContractsDirectDetailServiceData): ChartDropdownOption[] {
        if (data.contracts) {
            return this.generateOptions(data);
        }
        else return this.getDefaultSummaryData();
    }

    private extractConsultants(data: ContractDetail[]): { userDocNumber: string, name: string }[] {
        let userDocNumbers: { userDocNumber: string, name: string }[] = [];

        if (data) {
            data.forEach(x => {
                if (!userDocNumbers.find(y => x.userDocNumber == y.userDocNumber)) userDocNumbers.push({ userDocNumber: x.userDocNumber, name: x.userName });
            })
        }

        return userDocNumbers;
    }

    private generateOption(optionName: string, data: ContractDetail[], filter: (x: ContractDetail) => boolean): ChartDropdownOption {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        data.filter(filter).reduce((acc, x) => {
            let contractDate = moment(x.endDate);
            let accElement = acc.find((x) => {
                return x.date.date() == contractDate.date() && x.date.month() == contractDate.month();
            });

            if (accElement) accElement.count++;

            return acc;
        }, datasetData);

        let result = new ExpiringContractsDirectEvolutionData(new BarChartData(
            datasetData.map<string>(x => x.label),
            [
                new BarChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let option = new ChartDropdownOption();
        option.text = optionName;
        option.value = result;

        return option;
    }

    private generateOptions(data: ExpiringContractsDirectDetailServiceData): ChartDropdownOption[] {
        let consultants = this.extractConsultants(data.contracts);

        let global = this.generateOption("Todos", data.contracts, (x) => true);
        let otherOptions = consultants.map(x => this.generateOption(x.name, data.contracts, y => y.userDocNumber == x.userDocNumber))
            .sort((a, b) => (a.text ? a.text.toLowerCase() : "") < (b.text ? b.text.toLowerCase() : "") ? -1 : 1);

        return [global].concat(otherOptions);
    }

    private getMonthName(month: number): string {
        switch (month) {
            case 0: return "ene";
            case 1: return "feb";
            case 2: return "mar";
            case 3: return "abr";
            case 4: return "may";
            case 5: return "jun";
            case 6: return "jul";
            case 7: return "ago";
            case 8: return "sep";
            case 9: return "oct";
            case 10: return "nov";
            case 11: return "dic";
            default: return "";
        }
    }

    protected getDefaultSummaryData(): ChartDropdownOption[] {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        let result = new ExpiringContractsDirectEvolutionData(new BarChartData(
            datasetData.map<string>(x => x.label),
            [
                new BarChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let global = new ChartDropdownOption();
        global.text = "Todos";
        global.value = result;

        return [global];
    }

    protected extractUpdateTime(data: ExpiringContractsDirectDetailServiceData): Date {
        return data.updateTimestamp;
    }

    protected processDetailData(data: ExpiringContractsDirectDetailServiceData, args: any): ContractsDirectDetailData {
        if (data.contracts) {
            if (args && args.filter && args.filter.name && args.filter.name!="Todos") {
                let columnDefinitions = [
                    new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Cliente", null, true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
                    new NumberColumnDefinition("Tp. Contrato"),
                    new TextColumnDefinition("Consultor", null, true),
                    new TextColumnDefinition("Unidad", null, true),
                    new TextColumnDefinition("Nombre interlocutor", null, true),
                    new PhoneNumberColumnDefinition("Teléfono interlocutor"),
                ];

                let consultantNif = this.impersonationService.getNifSafe().toUpperCase();

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[7].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) { columnDefinitions[0].filterTerm = args.filter.date; }
                }

                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts) {
                    if (contract.userDocNumber === consultantNif) {
                        tabledata.push([
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.workContractId,
                            contract.userName,
                            contract.unitName,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                    else {
                        tabledata2.push([
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.workContractId,
                            contract.userName,
                            contract.unitName,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDirectDetailData(columnDefinitions, arrayOrdenado);
            } else {
                let columnDefinitions = [
                    new TextColumnDefinition("Consultor", null, true),
                    new TextColumnDefinition("Unidad", null, true),
                    new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Cliente", null, true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
                    new NumberColumnDefinition("Tp. Contrato"),
                    new TextColumnDefinition("Nombre interlocutor", null, true),
                    new PhoneNumberColumnDefinition("Teléfono interlocutor"),
                ];

                let consultantNif = this.impersonationService.getNifSafe().toUpperCase();

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[0].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) { columnDefinitions[2].filterTerm = args.filter.date; }
                }

                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts) {
                    if (contract.userDocNumber === consultantNif) {
                        tabledata.push([
                            contract.userName,
                            contract.unitName,
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.workContractId,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                    else {
                        tabledata2.push([
                            contract.userName,
                            contract.unitName,
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.workContractId,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDirectDetailData(columnDefinitions, arrayOrdenado);
            }
        }
        else return this.getDefaultDetailData();
    }

    protected getDefaultDetailData(): ContractsDirectDetailData {
        let columnDefinitions = [
            new TextColumnDefinition("Consultor", null, true),
            new TextColumnDefinition("Unidad", null, true),
            new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Trabajador"),
            new PhoneNumberColumnDefinition("Teléfono"),
            new EmailColumnDefinition("Email"),
            new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Tp. Contrato"),
            new TextColumnDefinition("Nombre interlocutor", null, true),
            new PhoneNumberColumnDefinition("Teléfono interlocutor"),
        ];

        return new ContractsDirectDetailData(columnDefinitions, [])
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }

    public showDetailEx(event: Event, prefilter: any) {
        this.mergeFilter(prefilter);
        this.showDetail(event);
    }
}
